import React, { useState, useEffect } from 'react';
import { Video, Quote, FolderOpen, Images, Loader2, BookOpen } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAdminAuth } from '../context/AdminAuthContext';
import { T } from '../components/ContentShared';
import ADNEditor from '../components/ADNEditor';
import HeroEditor from '../components/HeroEditor';
import ManifesteEditor from '../components/ManifesteEditor';
import ProjectsEditor from '../components/ProjectsEditor';
import GalleryPanel from '../components/GalleryPanel';

const API_URL = '';

const TABS = [
  { id: 'homepage', label: 'Contenu Site', icon: Video },
  { id: 'adn', label: 'ADN OGD', icon: BookOpen },
  { id: 'gallery', label: 'Galerie', icon: Images },
];

const SUB_TABS = [
  { id: 'hero', label: 'Hero Video', icon: Video },
  { id: 'manifeste', label: 'Editer ADN OGD', icon: Quote },
  { id: 'projects', label: 'Projets', icon: FolderOpen },
];

const AdminContentPage = () => {
  const { token } = useAdminAuth();
  const [activeTab, setActiveTab] = useState('homepage');
  const [subTab, setSubTab] = useState('hero');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [heroData, setHeroData] = useState(null);
  const [manifesteData, setManifesteData] = useState(null);
  const [projectsData, setProjectsData] = useState(null);
  const [images, setImages] = useState([]);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [heroRes, manifesteRes, projectsRes, galleryRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/homepage/hero`, { headers }),
          fetch(`${API_URL}/api/admin/homepage/manifeste`, { headers }),
          fetch(`${API_URL}/api/admin/homepage/projects`, { headers }),
          fetch(`${API_URL}/api/admin/gallery`, { headers }),
        ]);
        if (heroRes.ok) setHeroData((await heroRes.json()).content);
        if (manifesteRes.ok) setManifesteData((await manifesteRes.json()).content);
        if (projectsRes.ok) setProjectsData((await projectsRes.json()).content);
        if (galleryRes.ok) setImages(await galleryRes.json());
      } catch (err) { console.error('Failed to fetch CMS data:', err); } finally { setLoading(false); }
    };
    fetchAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const showMsg = (type, text) => { setMessage({ type, text }); setTimeout(() => setMessage({ type: '', text: '' }), 3000); };

  const saveSection = async (section, data) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/homepage/${section}`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(data) });
      showMsg(res.ok ? 'success' : 'error', res.ok ? 'Sauvegardé' : 'Erreur');
    } catch (err) { console.error('Save section failed:', err); showMsg('error', 'Erreur'); } finally { setSaving(false); }
  };

  const handleAddImage = async (newImage) => {
    if (!newImage.url || !newImage.title) return showMsg('error', 'URL et titre requis');
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/gallery`, { method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(newImage) });
      if (res.ok) { showMsg('success', 'Image ajoutée'); const galleryRes = await fetch(`${API_URL}/api/admin/gallery`, { headers }); if (galleryRes.ok) setImages(await galleryRes.json()); }
    } catch (err) { console.error('Add image failed:', err); showMsg('error', 'Erreur'); } finally { setSaving(false); }
  };

  const handleUpdateImage = async (imageId, updates) => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/gallery/${imageId}`, { method: 'PUT', headers: { ...headers, 'Content-Type': 'application/json' }, body: JSON.stringify(updates) });
      if (res.ok) { showMsg('success', 'Mis à jour'); const galleryRes = await fetch(`${API_URL}/api/admin/gallery`, { headers }); if (galleryRes.ok) setImages(await galleryRes.json()); }
    } catch (err) { console.error('Update image failed:', err); showMsg('error', 'Erreur'); } finally { setSaving(false); }
  };

  const handleDeleteImage = async (imageId) => {
    if (!window.confirm('Supprimer ?')) return;
    try {
      const res = await fetch(`${API_URL}/api/admin/gallery/${imageId}`, { method: 'DELETE', headers });
      if (res.ok) { showMsg('success', 'Supprimée'); const galleryRes = await fetch(`${API_URL}/api/admin/gallery`, { headers }); if (galleryRes.ok) setImages(await galleryRes.json()); }
    } catch (err) { console.error('Delete image failed:', err); showMsg('error', 'Erreur'); }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-5 h-5 animate-spin" style={{ color: T.vert }} />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="max-w-6xl" data-testid="admin-content-page">
        <div className="mb-8">
          <h1 className="text-2xl font-semibold" style={{ fontFamily: "'Playfair Display', serif", color: T.textPrimary }}>Editeur Manifeste</h1>
          <p className="text-sm mt-1" style={{ fontFamily: "'Montserrat', sans-serif", color: T.textMuted }}>Gerez le contenu et la galerie de votre site</p>
        </div>

        {message.text && (
          <div className="mb-6 p-4 rounded-xl text-sm" data-testid="content-message" style={{
            background: message.type === 'success' ? 'rgba(22,163,74,0.08)' : 'rgba(239,68,68,0.08)',
            border: `1px solid ${message.type === 'success' ? 'rgba(22,163,74,0.15)' : 'rgba(239,68,68,0.15)'}`,
            color: message.type === 'success' ? '#16a34a' : '#dc2626',
            fontFamily: "'Montserrat', sans-serif"
          }}>{message.text}</div>
        )}

        {/* Tabs */}
        <div className="flex gap-1 mb-8 p-1 rounded-xl" style={{ background: '#f0ede8' }}>
          {TABS.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)} data-testid={`tab-${tab.id}`}
              className="flex items-center gap-2 px-5 py-2.5 rounded-lg text-[13px] font-medium transition-all"
              style={{ fontFamily: "'Montserrat', sans-serif", background: activeTab === tab.id ? '#ffffff' : 'transparent', color: activeTab === tab.id ? T.vert : T.textMuted, boxShadow: activeTab === tab.id ? '0 1px 3px rgba(0,0,0,0.06)' : 'none' }}>
              <tab.icon className="w-4 h-4" /> {tab.label}
            </button>
          ))}
        </div>

        {/* HOMEPAGE TAB */}
        {activeTab === 'homepage' && (
          <div>
            <div className="flex gap-1 mb-6 p-1 rounded-lg" style={{ background: '#f0ede8' }}>
              {SUB_TABS.map(tab => (
                <button key={tab.id} onClick={() => setSubTab(tab.id)} data-testid={`subtab-${tab.id}`}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-[12px] font-medium transition-all"
                  style={{ fontFamily: "'Montserrat', sans-serif", background: subTab === tab.id ? '#ffffff' : 'transparent', color: subTab === tab.id ? T.vert : T.textMuted, boxShadow: subTab === tab.id ? '0 1px 3px rgba(0,0,0,0.06)' : 'none' }}>
                  <tab.icon className="w-3.5 h-3.5" /> {tab.label}
                </button>
              ))}
            </div>

            {subTab === 'hero' && <HeroEditor data={heroData} setData={setHeroData} onSave={saveSection} saving={saving} />}
            {subTab === 'manifeste' && <ManifesteEditor data={manifesteData} setData={setManifesteData} onSave={saveSection} saving={saving} />}
            {subTab === 'projects' && <ProjectsEditor data={projectsData} setData={setProjectsData} onSave={saveSection} saving={saving} />}
          </div>
        )}

        {/* ADN OGD TAB */}
        {activeTab === 'adn' && <ADNEditor token={token} showMsg={showMsg} />}

        {/* GALLERY TAB */}
        {activeTab === 'gallery' && <GalleryPanel images={images} onAdd={handleAddImage} onUpdate={handleUpdateImage} onDelete={handleDeleteImage} saving={saving} />}
      </div>
    </AdminLayout>
  );
};

export default AdminContentPage;
